import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Badge from '../components/badge/Badge';
import NewReferrerModal from '../components/modals/NewReferrerModal';
import { useReferrerNames } from '../hooks/useReferrerNames';
import { ReferrerName } from '../types/interfaces';
import { routes } from '../routes/routePaths';
import { toast } from 'react-toastify';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import { useConfig } from '../context/ConfigContext';

const formatCommission = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
};

const ReferrersConfiguration = () => {
  const fiscalId = useFiscalIdConfig();
  const config = useConfig();
  const showGuaranteesReferrerFields = config.isGuaranteesEnabled === true;
  const navigate = useNavigate();
  const {
    referrerNames,
    isLoading,
    isUpdating,
    refreshReferrerNames,
    updateReferrer,
    removeReferrer,
  } = useReferrerNames(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReferrer, setEditingReferrer] = useState<ReferrerName | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const handleOpenModal = () => {
    setEditingReferrer(null);
    setIsModalOpen(true);
  };

  const handleEditReferrer = (referrer: ReferrerName) => {
    setEditingReferrer(referrer);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingReferrer(null);
  };

  const handleSuccess = () => {
    refreshReferrerNames();
  };

  const handleToggleEnabled = async (referrer: ReferrerName) => {
    const newEnabled = !referrer.enabled;
    setTogglingId(referrer.id);
    try {
      const result = await updateReferrer(
        referrer.id,
        referrer.name,
        referrer.code,
        referrer.cuit,
        referrer.commissionPercentage,
        newEnabled,
      );
      if (result.success) {
        toast.success(newEnabled ? 'Referido reactivado' : 'Referido dado de baja');
      } else {
        toast.error(result.message || 'Error al cambiar el estado');
      }
    } catch {
      toast.error('Error al cambiar el estado');
    } finally {
      setTogglingId(null);
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: 'Código',
        selector: (row: ReferrerName) => row.code,
        cell: (row: ReferrerName) => <Card>{row.code}</Card>,
        sortable: true,
        width: '120px',
      },
      {
        name: 'Nombre',
        selector: (row: ReferrerName) => row.name,
        cell: (row: ReferrerName) => <Card>{row.name}</Card>,
        sortable: true,
      },
    ];

    const guaranteesColumns = showGuaranteesReferrerFields
      ? [
          {
            name: fiscalId.cuilLabel,
            selector: (row: ReferrerName) => row.cuit || '',
            cell: (row: ReferrerName) => (
              <Card>{row.cuit ? fiscalId.format(row.cuit) : '—'}</Card>
            ),
            sortable: true,
            width: '160px',
          },
          {
            name: '% Comisión',
            selector: (row: ReferrerName) => row.commissionPercentage ?? 0,
            cell: (row: ReferrerName) => (
              <Card>{formatCommission(row.commissionPercentage)}</Card>
            ),
            sortable: true,
            width: '140px',
          },
        ]
      : [];

    return [
      ...baseColumns,
      ...guaranteesColumns,
      {
        name: 'Estado',
        selector: (row: ReferrerName) => (row.enabled ? 'Activo' : 'Inactivo'),
        cell: (row: ReferrerName) => (
          <Badge type='text' size='sm' variant={row.enabled ? 'success' : 'neutral'}>
            {row.enabled ? 'Activo' : 'Inactivo'}
          </Badge>
        ),
        sortable: true,
        width: '120px',
      },
      {
        name: 'Acciones',
        cell: (row: ReferrerName) => (
          <Card display='flex' gap='8px' alignItems='center'>
            <Icon
              name='edit_outline'
              width={20}
              height={20}
              cursor='pointer'
              variant='info'
              onClick={() => handleEditReferrer(row)}
              aria-label={`Editar referido ${row.name}`}
            />
            <Button
              variant={row.enabled ? 'secondary' : 'primary'}
              size='sm'
              onClick={() => handleToggleEnabled(row)}
              disabled={togglingId === row.id || isUpdating}
            >
              {row.enabled ? 'Dar de baja' : 'Reactivar'}
            </Button>
          </Card>
        ),
        width: '200px',
      },
    ];
  }, [
    fiscalId,
    showGuaranteesReferrerFields,
    togglingId,
    isUpdating,
  ]);

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing='4'
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <Card display='flex' alignItems='center' gap='8px'>
          <Button
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label='Volver a configuración'
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Referidos
          </H>
        </Card>

        <Button variant='primary' onClick={handleOpenModal}>
          Nuevo referido
        </Button>
      </Card>

      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : referrerNames.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={referrerNames}
            tableData={{
              size: referrerNames.length,
              page: 0,
              totalElements: referrerNames.length,
              totalPages: 1,
            }}
            handleChangePage={() => {}}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Icon name='folder_empty' width={80} height={80} variant='primary' />
          </Card>
          <H variant='primary' align='center' withMargin={false} weight='bold' type='3'>
            No hay referidos registrados.
          </H>
        </Card>
      )}

      <NewReferrerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        referrerToEdit={editingReferrer}
      />
    </Card>
  );
};

export default ReferrersConfiguration;
